import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Box, Button, Paper, Typography } from '@mui/material'
import { useField } from 'formik'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { useConfirmDialogContext } from '../../../utils/providers/confirmDialogContext'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'
import { GeoLocation, LocationsPanelMode, LocationsSort, LocationsSortKey } from './types'
import { filterLocationItems, getLocationName, paginate, sortLocationItems, toLocationItems } from './sortLocations'
import { stepRadius } from './radius'
import { useLocationsSelection } from './useLocationsSelection'
import { useLocationAreas } from './useLocationAreas'
import LocationsSummary from './LocationsSummary'
import LocationsSelectionBar from './LocationsSelectionBar'
import LocationsTable from './LocationsTable'
import LocationsPagination, { PAGE_SIZES } from './LocationsPagination'
import { EditSearchInput, ReadSearchInput } from './LocationsSearchInput'
import LocationsChips from './LocationsChips'
import LocationsViewToggle, { LocationsView } from './LocationsViewToggle'
import LocationsRuleBar, { LocationTypeOption } from './LocationsRuleBar'

export type LocationsPanelProps = {
  name: string
  locationType: LocationTypesEnum
  mode: LocationsPanelMode
  compact?: boolean
  countryCode?: string
  /** Id of the edit-mode input, also used to anchor the Google Places service */
  autocompleteId?: string
  error?: boolean
  /** Opens the CSV import, shown next to Clear all in edit mode */
  onImportCsv?: () => void
  /** The locations are excluded rather than targeted: the header says so and is tinted */
  exclusive?: boolean
  /** Edit mode: shows the Include / Exclude toggle in the panel's top bar */
  onExclusiveChange?: (exclusive: boolean) => void
  /** Edit mode: shows the location type select in the panel's top bar, with these options */
  locationTypeOptions?: LocationTypeOption[]
  onLocationTypeChange?: (locationType: LocationTypesEnum) => void
}

type UndoState = { previous: GeoLocation[]; after: GeoLocation[]; message: string }

// Resolved at call time: GeoTargetingFields imports this module indirectly, so its enum is not ready at load time
const getNameColumn = (locationType: LocationTypesEnum): string => {
  switch (locationType) {
    case LocationTypesEnum.Departements:
      return 'columnDepartement'
    case LocationTypesEnum.Regions:
      return 'columnRegion'
    case LocationTypesEnum.Countries:
      return 'columnCountry'
    default:
      return 'columnCity'
  }
}

const VIEW_STORAGE_KEY = 'geoLocationsPanel.view'

// The viewer's choice of view is a per-browser convenience: storage may be unavailable, so never rely on it
const readStoredView = (): LocationsView => {
  try {
    return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'chips' ? 'chips' : 'table'
  } catch {
    return 'table'
  }
}

const storeView = (view: LocationsView): void => {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view)
  } catch {
    // Not remembered: the panel still works with the default view
  }
}

const HIGHLIGHT_DURATION = 4000
const ADDED_SORT: LocationsSort = { key: 'added', dir: 'asc' }
// Larger than any page count: paginate() clamps it to the last page, where a new location lands
const LAST_PAGE = Number.MAX_SAFE_INTEGER

const LocationsPanel: React.FC<LocationsPanelProps> = ({
  name,
  locationType,
  mode,
  compact = false,
  countryCode = 'fr',
  autocompleteId = 'geoLocationsPanelSearch',
  error,
  onImportCsv,
  exclusive = false,
  onExclusiveChange,
  locationTypeOptions,
  onLocationTypeChange,
}) => {
  const t = useAppTranslations()
  const confirm = useConfirmDialogContext()
  const [field, , helpers] = useField<GeoLocation[]>({ name })
  const locations = useMemo(() => (Array.isArray(field.value) ? field.value : []), [field.value])
  const isEdit = mode === 'edit'
  const isCities = locationType === LocationTypesEnum.Cities

  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const [sort, setSort] = useState<LocationsSort>(ADDED_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
  // The location object, not its index: indices shift when another row is removed
  const [highlighted, setHighlighted] = useState<GeoLocation>()
  const [undo, setUndo] = useState<UndoState>()
  const [view, setView] = useState<LocationsView>(readStoredView)
  const selection = useLocationsSelection(locations.length)
  const { column: areaColumn, areas } = useLocationAreas(locations, locationType, countryCode)

  const items = useMemo(() => toLocationItems(locations, areas), [locations, areas])
  const matching = useMemo(() => filterLocationItems(items, deferredQuery), [items, deferredQuery])
  const sorted = useMemo(() => sortLocationItems(matching, sort), [matching, sort])
  const current = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize])
  const groupCount = useMemo(
    () => (areas ? new Set(areas.map((area) => area?.group).filter(Boolean)).size : undefined),
    [areas]
  )

  const highlightedPosition = highlighted ? locations.indexOf(highlighted) : -1
  const highlightedIndex = highlightedPosition >= 0 ? highlightedPosition : undefined

  useEffect(() => {
    if (highlighted === undefined) return undefined
    const timer = setTimeout(() => setHighlighted(undefined), HIGHLIGHT_DURATION)
    return () => clearTimeout(timer)
  }, [highlighted])

  const { clear: clearSelection } = selection
  // A new search drops the selection, so bulk actions never reach rows the search hides
  const onQueryChange = useCallback(
    (value: string) => {
      setQuery(value)
      setPage(1)
      clearSelection()
    },
    [clearSelection]
  )

  const onSort = useCallback((key: LocationsSortKey) => {
    setSort((previous) => ({ key, dir: previous.key === key && previous.dir === 'asc' ? 'desc' : 'asc' }))
    setPage(1)
  }, [])

  // Once Google returned the place and it was appended: show it where it lands, at the end in insertion order
  const onAdded = useCallback((location: GeoLocation) => {
    setSort(ADDED_SORT)
    setPage(LAST_PAGE)
    setHighlighted(location)
  }, [])

  // The place is already in the list: show that row instead (the search is cleared, so it is in insertion order)
  const onDuplicate = useCallback(
    (index: number) => {
      setSort(ADDED_SORT)
      setPage(Math.floor(index / pageSize) + 1)
      setHighlighted(locations[index])
    },
    [locations, pageSize]
  )

  const update = useCallback(
    (next: GeoLocation[], undoMessage?: string) => {
      helpers.setValue(next)
      setUndo(undoMessage ? { previous: locations, after: next, message: undoMessage } : undefined)
    },
    [helpers, locations]
  )

  const removeIndices = useCallback(
    (indices: ReadonlySet<number>) => {
      if (indices.size === 0) return
      const message =
        indices.size === 1
          ? t('adgroup.geoTargeting.panel.removedOne', { name: getLocationName(locations[Array.from(indices)[0]]) })
          : t('adgroup.geoTargeting.panel.removedMany', { count: indices.size })
      update(
        locations.filter((_, index) => !indices.has(index)),
        message
      )
    },
    [locations, t, update]
  )

  const onStepRadius = useCallback(
    (index: number, direction: 1 | -1) =>
      update(
        locations.map((location, locationIndex) =>
          locationIndex === index ? { ...location, radius: stepRadius(location.radius, direction) } : location
        )
      ),
    [locations, update]
  )

  const onRemove = useCallback((index: number) => removeIndices(new Set([index])), [removeIndices])

  // Removing everything at once asks first; Undo stays available afterwards
  const onClearAll = (): void =>
    confirm({
      message: t('adgroup.geoTargeting.panel.clearAllConfirm', { count: locations.length }),
      buttonLabel: t('adgroup.geoTargeting.panel.clearAll'),
      onSubmit: () => removeIndices(new Set(locations.map((_, index) => index))),
    })

  const onSetRadius = useCallback(
    (radius: number) =>
      update(locations.map((location, index) => (selection.selected.has(index) ? { ...location, radius } : location))),
    [locations, selection.selected, update]
  )

  const pageIndices = current.items.map(({ index }) => index)
  const isPageSelected = pageIndices.length > 0 && pageIndices.every((index) => selection.selected.has(index))
  const onTogglePage = (): void => selection.setMany(pageIndices, !isPageSelected)
  const canSelectAllMatching = isPageSelected && selection.selected.size < matching.length

  // An undo is only offered while nothing else has changed the list since
  const activeUndo = undo && undo.after === field.value ? undo : undefined
  const isFiltered = deferredQuery.trim().length > 0
  const hasSelection = isEdit && selection.selected.size > 0
  const isChips = view === 'chips'
  const listLabel = t(`adgroup.geoTargeting.${locationType}`)

  const onViewChange = (nextView: LocationsView): void => {
    setView(nextView)
    storeView(nextView)
  }

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        overflow: 'hidden',
        borderColor: error ? theme.palette.error.main : undefined,
      })}
    >
      {isEdit && !compact && (
        <LocationsRuleBar
          exclusive={exclusive}
          onExclusiveChange={onExclusiveChange}
          locationType={locationType}
          locationTypeOptions={locationTypeOptions}
          onLocationTypeChange={onLocationTypeChange}
          onImportCsv={onImportCsv}
          onClearAll={locations.length > 0 ? onClearAll : undefined}
        />
      )}
      {hasSelection ? (
        <LocationsSelectionBar
          selectedCount={selection.selected.size}
          selectAllCount={canSelectAllMatching ? matching.length : undefined}
          canSetRadius={isCities}
          onSelectAll={() =>
            selection.setMany(
              matching.map(({ index }) => index),
              true
            )
          }
          onSetRadius={onSetRadius}
          onRemoveSelected={() => removeIndices(selection.selected)}
          onDeselect={selection.clear}
        />
      ) : (
        !compact && (
          <LocationsSummary
            total={locations.length}
            locationType={locationType}
            groupCount={groupCount}
            exclusive={exclusive}
            countryCode={countryCode}
          />
        )
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isEdit ? (
            <EditSearchInput
              query={query}
              onQueryChange={onQueryChange}
              matchingCount={matching.length}
              total={locations.length}
              name={name}
              locationType={locationType}
              countryCode={countryCode}
              autocompleteId={autocompleteId}
              locations={locations}
              onAdded={onAdded}
              onDuplicate={onDuplicate}
              exclusive={exclusive}
              error={error}
            />
          ) : (
            <ReadSearchInput
              query={query}
              onQueryChange={onQueryChange}
              matchingCount={matching.length}
              total={locations.length}
              label={t('adgroup.geoTargeting.panel.searchOnly', { count: locations.length })}
            />
          )}
        </Box>
        <LocationsViewToggle view={view} onChange={onViewChange} />
      </Box>

      {current.items.length > 0 && isChips && (
        <LocationsChips
          items={current.items}
          mode={mode}
          showRadius={isCities}
          selected={selection.selected}
          highlightedIndex={highlightedIndex}
          onToggle={selection.toggle}
          onRemove={onRemove}
          label={listLabel}
        />
      )}
      {current.items.length > 0 && !isChips && (
        <LocationsTable
          items={current.items}
          mode={mode}
          nameColumn={getNameColumn(locationType)}
          areaColumn={areaColumn}
          showRadius={isCities}
          sort={sort}
          onSort={onSort}
          selected={selection.selected}
          highlightedIndex={highlightedIndex}
          onToggle={selection.toggle}
          onTogglePage={onTogglePage}
          onStepRadius={onStepRadius}
          onRemove={onRemove}
          label={listLabel}
        />
      )}
      {current.items.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ px: 1.5, py: 3, textAlign: 'center' }}>
          {locations.length === 0
            ? t('adgroup.geoTargeting.panel.empty')
            : t('adgroup.geoTargeting.panel.noMatch', { query: deferredQuery.trim() })}
        </Typography>
      )}

      {activeUndo && (
        <Box
          role="status"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 32,
            pl: 1.5,
            pr: 1,
            bgcolor: 'grey.900',
            color: 'common.white',
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {activeUndo.message}
          </Typography>
          <Button size="small" onClick={() => update(activeUndo.previous)} sx={{ color: 'primary.light' }}>
            {t('adgroup.geoTargeting.panel.undo')}
          </Button>
        </Box>
      )}

      {locations.length > 0 && (
        <LocationsPagination
          page={current.page}
          pageSize={pageSize}
          matchingCount={matching.length}
          isFiltered={isFiltered}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      )}
    </Paper>
  )
}

export default LocationsPanel
